/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-target-blank */
import React from 'react';

import { useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { isHaMobileApp as isMobileApp } from '@ha/core/MobileApp';
import { searchParamsToObject } from '@ha/core/SearchQuery/searchParamsToObject';
import { buildParams } from '@ha/search-query';
import { units } from '@hbf/dsl/core';

import { useIntl } from 'ha/i18n';
import { ListingCardModel } from 'ha/models/Listing/types';

import { getPropertyPageURLFromSearchResult } from 'ha/pages/Property/utilities/getPropertyPageURL';

import { Satisfies } from './utilities/satisfies';

const useStyles = makeStyles()(theme => ({
  cardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    height: '100%',
    borderRadius: theme.utils.borderRadius('ref/radius/xl'),

    '&:focus': {
      outline: 'none',
    },

    '&:focus-visible': {
      outline: units.border(1, 'solid', theme.palette.tertiary.main),
      outlineOffset: theme.utils.spacing('ref/spacing/2'),
    },
  },
}));

/** Props that are not (directly) modifiable by the consumer */
type AnchorOverrideProps = Satisfies<
  'href' | 'target' | 'itemScope' | 'itemType',
  keyof React.AnchorHTMLAttributes<HTMLAnchorElement>
>;

type ListingCardAnchorProps = {
  children: React.ReactNode;
  listing: ListingCardModel;
  cityLocalized?: string;
  countryLocalized?: string;
  searchParams?: Record<string, string> | URLSearchParams;
  openInNewTab?: boolean;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, AnchorOverrideProps>;

export const ListingCardAnchor = React.memo(
  ({
    children,
    listing,
    cityLocalized,
    countryLocalized,
    searchParams,
    openInNewTab,
    onClick,
    ...rest
  }: ListingCardAnchorProps) => {
    const { urlResolver, lang } = useIntl();

    const { classes, cx } = useStyles();

    const theme = useTheme();
    const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));

    const allowOpenInNewTab = !isMobileDevice && !isMobileApp();

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);

        // webvitals library can sometimes "forget" to stop an INP measurement on self navigation (leading to inflated 1000+ second scores)
        // (presumably) it then only stops after the user returns back and the page is restored from cache
        // by delaying the navigation to the next frame we force the INP timer to stop correctly
        if (!event.defaultPrevented && event.currentTarget.target === '_self') {
          event.preventDefault();
          const { href } = event.currentTarget;

          requestAnimationFrame(() => {
            window.location.assign(href);
          });
        }
      },
      [onClick],
    );

    const isPropertyListing = listing.identifiers.recordType === 'property';

    const path = isPropertyListing
      ? getPropertyPageURLFromSearchResult(
          {
            propertyId: listing.identifiers.propertyId!,
            cityLocalized: cityLocalized || listing.location.city.localized,
            countryLocalized:
              countryLocalized || listing.location.country.localized,
            streetName: listing.location.street,
            propertyAlias: listing.property.alias,
          },
          { lang },
        )
      : listing.identifiers.unitTypePath!;

    const url = new URL(
      urlResolver.absolutizePath(urlResolver.formatHrefURL(path), {
        skipAddLanguage: true,
      }),
    );

    const urlSearchParams = new URLSearchParams(searchParams);
    url.search = buildParams(searchParamsToObject(urlSearchParams));

    return (
      <a
        {...rest}
        href={url.toString()}
        target={openInNewTab && allowOpenInNewTab ? '_blank' : '_self'}
        itemScope
        itemType="http://schema.org/Offer"
        className={cx(classes.cardLink, rest.className)}
        data-test-locator="ListingCard/Anchor"
        onClick={handleClick}
      >
        {children}
      </a>
    );
  },
);
